import React from "react";

import CaretDownIcon from "@common/assets/images/shared-icons/caret-down-icon.svg";
import CaretUpIcon from "@common/assets/images/shared-icons/caret-up-icon.svg";
import Heading from "@common/components/Heading/Heading";
import Option from "@common/components/Option/Option";
import Tooltip from "@common/components/Tooltip/Tooltip";
import {
  IntegrationLayoutEntry,
  MetadataEnabledState,
  MetadataKeys,
  MetadataParentKey,
} from "@common/models/integrations";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { isIntegrationLayoutCustomizationEnabled } from "@main/context/Subscription/SubscriptionContext";
import * as Styled from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/components/IntegrationLayoutSettings.styles";
import { MoveItemButton } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/components/IntegrationLayoutSettings.styles";

interface IntegrationDataConfigProps {
  integrationLayout: IntegrationLayoutEntry[];
  moveEntryUp: (index: number) => void;
  moveEntryDown: (index: number) => void;
  toggleEntry: (index: number) => void;
  metadataKeys?: MetadataKeys;
  metadataEnabledState?: MetadataEnabledState;
  toggleMetadataKey?: (parentKey: MetadataParentKey, key: string) => void;
  toggleAllMetadataKeys?: (parentKey: MetadataParentKey, keys: string[]) => void;
  setMetadataKeysEnabled?: (parentKey: MetadataParentKey, newState: Record<string, boolean>) => void;
  resetMetadataKeys?: (parentKey: MetadataParentKey) => void;
}

export const IntegrationLayoutSettings = ({
  integrationLayout,
  moveEntryUp,
  moveEntryDown,
  toggleEntry,
  metadataKeys,
  metadataEnabledState,
  toggleMetadataKey,
  toggleAllMetadataKeys,
  setMetadataKeysEnabled,
  resetMetadataKeys,
}: IntegrationDataConfigProps) => {
  const { selectedWorkspace } = useAppSelectionContext();

  if (!integrationLayout || integrationLayout.length === 0) return <></>;
  const isEnabled = isIntegrationLayoutCustomizationEnabled(selectedWorkspace);

  return (
    <Styled.IntegrationLayoutSettings>
      <Heading
        heading4
        fontWeight={"bold"}
        marginBottom={22}
      >
        {"What to send and in what order"}
      </Heading>
      <Styled.IntegrationLayoutList>
        {integrationLayout.map((element, index) => {
          const subItems =
            element.key === "ticket_metadata"
              ? metadataKeys?.ticket_metadata
              : element.key === "app_user_metadata"
              ? metadataKeys?.app_user_metadata
              : undefined;

          return (
            <IntegrationDataRow
              key={element.key}
              index={index}
              data={element}
              onMoveDownClick={moveEntryDown}
              onMoveUpClick={moveEntryUp}
              onDataItemChanged={toggleEntry}
              disabled={!isEnabled}
              subItems={subItems}
              subItemEnabled={metadataEnabledState?.[element.key as MetadataParentKey] ?? {}}
              onSubItemToggle={(key) => toggleMetadataKey?.(element.key as MetadataParentKey, key)}
              onToggleAll={
                subItems ? () => toggleAllMetadataKeys?.(element.key as MetadataParentKey, subItems) : undefined
              }
              onSubItemSet={(newState) => setMetadataKeysEnabled?.(element.key as MetadataParentKey, newState)}
              onResetSubItems={() => resetMetadataKeys?.(element.key as MetadataParentKey)}
            />
          );
        })}
      </Styled.IntegrationLayoutList>
    </Styled.IntegrationLayoutSettings>
  );
};

interface IntegrationDataRowProps {
  index: number;
  data: IntegrationLayoutEntry;
  onMoveUpClick: (index: number) => void;
  onMoveDownClick: (index: number) => void;
  onDataItemChanged: (index: number) => void;
  disabled?: boolean;
  subItems?: string[];
  subItemEnabled?: Record<string, boolean>;
  onSubItemToggle?: (key: string) => void;
  onToggleAll?: () => void;
  onSubItemSet?: (newState: Record<string, boolean>) => void;
  onResetSubItems?: () => void;
}

const IntegrationDataRow = ({
  index,
  data,
  onMoveDownClick,
  onMoveUpClick,
  onDataItemChanged,
  disabled,
  subItems,
  subItemEnabled = {},
  onSubItemToggle,
  onToggleAll,
  onSubItemSet,
  onResetSubItems,
}: IntegrationDataRowProps) => {
  const hasSubItems = subItems && subItems.length > 0;
  const isImplicitlyAllEnabled = !!data.enabled && !!hasSubItems && !subItems.some((k) => subItemEnabled[k]);
  const effectiveSubItemEnabled = isImplicitlyAllEnabled
    ? Object.fromEntries(subItems!.map((k) => [k, true]))
    : subItemEnabled;
  const someEnabled = hasSubItems && subItems.some((k) => effectiveSubItemEnabled[k]);
  const allEnabled = hasSubItems && subItems.every((k) => effectiveSubItemEnabled[k]);

  const handleParentCheck = () => {
    if (allEnabled) {
      onDataItemChanged(index);
    } else if (!data.enabled) {
      onResetSubItems?.();
      onDataItemChanged(index);
    } else {
      onResetSubItems?.();
    }
  };

  const handleSubItemToggle = (key: string) => {
    if (isImplicitlyAllEnabled) {
      onSubItemSet?.(Object.fromEntries(subItems!.map((k) => [k, k !== key])));
    } else {
      const isLastEnabled =
        effectiveSubItemEnabled[key] && subItems!.filter((k) => effectiveSubItemEnabled[k]).length === 1;
      const willBeAllEnabled =
        !effectiveSubItemEnabled[key] && subItems!.filter((k) => k !== key).every((k) => effectiveSubItemEnabled[k]);
      if (isLastEnabled) {
        onResetSubItems?.();
        onDataItemChanged(index);
      } else if (willBeAllEnabled) {
        onResetSubItems?.();
      } else {
        onSubItemToggle?.(key);
      }
    }
  };

  return (
    <>
      <Styled.IntegrationLayoutEntry>
        <div style={{ marginRight: "1.6rem" }}>
          <Styled.IntegrationLayoutEntryOrderBox>
            <Styled.MoveItemTooltip
              text={disabled ? "Upgrade to Organization plan" : "Move up"}
              position={"top"}
            >
              <MoveItemButton
                onClick={() => onMoveUpClick(index)}
                disabled={disabled}
              >
                <Styled.MoveItemIcon src={CaretUpIcon} />
              </MoveItemButton>
            </Styled.MoveItemTooltip>
            <Styled.MoveItemTooltip
              text={disabled ? "Upgrade to Organization plan" : "Move down"}
              position={"top"}
            >
              <MoveItemButton
                onClick={() => onMoveDownClick(index)}
                disabled={disabled}
              >
                <Styled.MoveItemIcon src={CaretDownIcon} />
              </MoveItemButton>
            </Styled.MoveItemTooltip>
          </Styled.IntegrationLayoutEntryOrderBox>
        </div>
        <div style={{ flex: 1, marginRight: "1.6rem" }}>
          <Tooltip
            text={disabled ? "Upgrade to Organization plan" : ""}
            position={"top"}
            disabled={!disabled}
          >
            <Option
              label={getIntegrationDataName(data.key)}
              checked={hasSubItems ? !!allEnabled : !!data.enabled}
              indeterminate={!!(someEnabled && !allEnabled)}
              handleCheck={hasSubItems ? handleParentCheck : () => onDataItemChanged(index)}
              disabled={disabled}
            />
          </Tooltip>
        </div>
        {!hasSubItems && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <Tooltip
              text={"Example data"}
              position={"top"}
            >
              <Styled.MoveItemText
                style={{ cursor: "help" }}
                p2
              >
                {getIntegrationDataExample(data.key)}
              </Styled.MoveItemText>
            </Tooltip>
          </div>
        )}
      </Styled.IntegrationLayoutEntry>
      {hasSubItems && (
        <Styled.MetadataKeyList>
          {subItems.map((key) => (
            <Styled.MetadataKeyEntry key={key}>
              <Option
                label={key}
                checked={effectiveSubItemEnabled[key] ?? false}
                handleCheck={() => handleSubItemToggle(key)}
                disabled={disabled}
              />
            </Styled.MetadataKeyEntry>
          ))}
        </Styled.MetadataKeyList>
      )}
    </>
  );
};

const getIntegrationDataExample = (key: string) => {
  switch (key) {
    case "ai_summary":
      return "Hi there! I'm your AI colleague, a triaging agent.";
    case "ticket_id":
      return "368";
    case "permalink":
      return "https://app.shakebugs.com/ourapp/17";
    case "linked_issues":
      return "https://app.shakebugs.com/ourapp/1, https://app.shakebugs.com/ourapp/12";
    case "custom_fields":
      return "Feedback Type: Bug";
    case "screenshot":
      return "screenshot.jpg";
    case "activity_history":
      return "";
    case "tags":
      return "bug, sample-ticket";
    case "os":
      return "Android 13";
    case "device":
      return "Galaxy S21 5G";
    case "app_version":
      return "6.60 (2030)";
    case "current_view":
      return "MainActivity";
    case "timezone":
      return "America/New York";
    case "locale":
      return "en-US";
    case "network_status":
      return "Cellular 3G • Verizon";
    case "resolution":
      return "1080 × 2160 px";
    case "screen_density":
      return "xhdpi";
    case "orientation":
      return "Portrait";
    case "authentication":
      return "Required";
    case "battery_status":
      return "58% • Discharging";
    case "used_storage":
      return "43% • 53.8 out of 125.0 GB";
    case "memory_usage":
      return "92 MB";
    case "permissions":
      return "ACCESS_FINE_LOCATION, READ_CONTACTS, READ_SMS";
    case "sdk_version":
      return "16.2.2";
    case "reporter_deprecated":
      return "user@example.com";
    case "fullstory":
      return " https://app.fullstory.com/ui/...";
    case "ticket_metadata":
      return "UserId: 4879562, test_key: test_value";
    case "app_user_details":
      return "Taylor Smith (taylor.smith@example.com)";
    case "app_user_metadata":
      return "role: Project Manager";
    case "features":
      return "Discover our latest Features";
    case "issue_reported_time":
      return "Oct 28, 3:30:28 PM";
    default:
      console.log(`What is this ${key}`);
  }
};

const getIntegrationDataName = (key: string) => {
  switch (key) {
    case "ai_summary":
      return "AI Summary";
    case "ticket_id":
      return "Ticket ID";
    case "permalink":
      return "Ticket link";
    case "linked_issues":
      return "Duplicate tickets";
    case "custom_fields":
      return "Custom fields";
    case "screenshot":
      return "Screenshot";
    case "activity_history":
      return "Activity history";
    case "tags":
      return "Tags";
    case "os":
      return "OS";
    case "device":
      return "Device";
    case "app_version":
      return "App version";
    case "current_view":
      return "Current view";
    case "timezone":
      return "Timezone";
    case "locale":
      return "Locale";
    case "network_status":
      return "Network";
    case "resolution":
      return "Resolution";
    case "screen_density":
      return "Screen density";
    case "orientation":
      return "Orientation";
    case "authentication":
      return "Authentication";
    case "battery_status":
      return "Battery status";
    case "used_storage":
      return "Used storage";
    case "memory_usage":
      return "Used memory";
    case "permissions":
      return "Permissions";
    case "sdk_version":
      return "SDK version";
    case "reporter_deprecated":
      return "Reporter (deprecated)";
    case "fullstory":
      return "Fullstory session";
    case "ticket_metadata":
      return "Ticket metadata";
    case "app_user_details":
      return "User details";
    case "app_user_metadata":
      return "User metadata";
    case "features":
      return "Features";
    case "issue_reported_time":
      return "Ticket submitted at";
    default:
      console.log(`What is this ${key}`);
  }
};
